import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, GraduationCap, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { createBTechOrder, verifyBTechPayment } from "../lib/api";

// ── Validation helpers ─────────────────────────────────────────────────────────
const req = (v) => v != null && String(v).trim() !== "";
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const normalizePhone = (v) => {
    let d = String(v || "").replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
    if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
    return d;
};
const isMobile = (v) => /^[6-9]\d{9}$/.test(normalizePhone(v));
const isName = (v) => /^[A-Za-z][A-Za-z .'-]{1,}$/.test(String(v || "").trim());

// ── Reusable field components ──────────────────────────────────────────────────
function TextField({ label, name, form, set, errors, placeholder, type = "text", required, colSpan }) {
    const error = errors[name];
    return (
        <div className={colSpan ? "sm:col-span-2" : ""}>
            <Label className="text-sm font-medium text-gray-700">
                {label} {required && <span className="text-red-500">*</span>}
            </Label>
            <Input
                type={type}
                value={form[name] ?? ""}
                onChange={(e) => set(name, e.target.value)}
                placeholder={placeholder}
                aria-invalid={!!error}
                className={`mt-1.5 h-12 rounded-xl ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            />
            {error && <p className="mt-1 text-xs font-medium text-red-500">{error}</p>}
        </div>
    );
}


// ── Razorpay script loader ─────────────────────────────────────────────────────
function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (document.getElementById("razorpay-script")) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.id = "razorpay-script";
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}


// ── Main Page ──────────────────────────────────────────────────────────────────
export default function BTechEnrollmentPage() {
    const navigate = useNavigate();

    const [form, setFormState] = useState({
        // Student
        full_name: "",
        email: "",
        mobile_number: "",
        city_location: "",
        // Parent
        parent_name: "",
        parent_mobile: "",
        parent_email: "",
        // Payment
        amount: "",
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [enrollmentId, setEnrollmentId] = useState(null);

    const set = (name, value) => {
        setFormState((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    function validate() {
        const errs = {};

        // Student fields
        if (!req(form.full_name)) errs.full_name = "Full name is required";
        else if (!isName(form.full_name)) errs.full_name = "Enter a valid name (letters only)";

        if (req(form.email) && !isEmail(form.email)) errs.email = "Enter a valid email address";

        if (req(form.mobile_number) && !isMobile(form.mobile_number)) errs.mobile_number = "Enter a valid 10-digit Indian mobile number";

        if (!req(form.city_location)) errs.city_location = "City is required";

        // Parent fields
        if (!req(form.parent_name)) errs.parent_name = "Parent/guardian name is required";
        else if (!isName(form.parent_name)) errs.parent_name = "Enter a valid name";

        if (!req(form.parent_mobile)) errs.parent_mobile = "Parent mobile number is required";
        else if (!isMobile(form.parent_mobile)) errs.parent_mobile = "Enter a valid 10-digit mobile number";

        if (!req(form.parent_email)) errs.parent_email = "Parent email is required for payment receipt";
        else if (!isEmail(form.parent_email)) errs.parent_email = "Enter a valid email address";

        const amt = Number(form.amount);
        if (!form.amount || isNaN(amt) || amt < 1) errs.amount = "Please enter a valid amount (minimum ₹1)";

        return errs;
    }

    async function handlePayment() {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            // Scroll to first error
            const firstErrorEl = document.querySelector("[data-error='true']");
            if (firstErrorEl) firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
            toast.error("Please fix the highlighted fields before proceeding.");
            return;
        }

        setSubmitting(true);

        try {
            // 1. Load Razorpay checkout script
            const loaded = await loadRazorpayScript();
            if (!loaded) {
                toast.error("Failed to load payment gateway. Check your internet connection.");
                setSubmitting(false);
                return;
            }

            // 2. Create order on backend
            const amountPaise = Math.round(Number(form.amount) * 100);
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim().toLowerCase() || undefined,
                mobile_number: form.mobile_number.trim() ? normalizePhone(form.mobile_number) : undefined,
                city_location: form.city_location.trim(),
                parent_name: form.parent_name.trim(),
                parent_mobile: normalizePhone(form.parent_mobile),
                parent_email: form.parent_email.trim(),
                amount_paise: amountPaise,
            };

            const order = await createBTechOrder(payload);

            // 3. Open Razorpay checkout
            const options = {
                key: order.key_id,
                amount: order.amount,
                currency: order.currency,
                name: "AI.Prof",
                description: "BTech Computer Science with Specialization in AI and ML",
                order_id: order.order_id,
                prefill: {
                    name: form.parent_name.trim(),
                    email: form.parent_email.trim(),
                    contact: normalizePhone(form.parent_mobile),
                },
                theme: { color: "#2563EB" },
                method: {
                    upi: true,        // includes QR code + UPI apps
                    card: true,
                    netbanking: true,
                    wallet: true,
                },
                config: {
                    display: {
                        blocks: {
                            upi: { name: "Pay via UPI / QR", instruments: [{ method: "upi" }] },
                        },
                        sequence: ["block.upi"],
                        preferences: { show_default_blocks: true },
                    },
                },
                handler: async function (response) {
                    // 4. Verify payment on backend
                    try {
                        await verifyBTechPayment({
                            enrollment_id: order.enrollment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        setEnrollmentId(order.enrollment_id);
                        setSuccess(true);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    } catch (err) {
                        toast.error(err.message || "Payment verification failed. Please contact support.");
                    } finally {
                        setSubmitting(false);
                    }
                },
                modal: {
                    ondismiss: () => {
                        toast.info("Payment cancelled. You can try again.");
                        setSubmitting(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", (response) => {
                toast.error(`Payment failed: ${response.error.description}`);
                setSubmitting(false);
            });
            rzp.open();
        } catch (err) {
            toast.error(err.message || "Something went wrong. Please try again.");
            setSubmitting(false);
        }
    }

    // ── Success screen ───────────────────────────────────────────────────────────
    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center px-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center"
                >
                    <div className="flex justify-center mb-6">
                        <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="h-10 w-10 text-green-500" />
                        </div>
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 mb-3">
                        Seat Reserved!
                    </h1>
                    <p className="text-gray-600 mb-2">
                        Your seat in the <strong>AI.Prof BTech Programme</strong> is reserved.
                    </p>
                    <p className="text-sm text-gray-500 mb-8">
                        Your payment was successful. Our team will reach out to{" "}
                        <strong>{form.email}</strong> within 24 hours with next steps.
                    </p>
                    {enrollmentId && (
                        <p className="text-xs text-gray-400 mb-8">
                            Enrollment ID: <span className="font-mono">{enrollmentId}</span>
                        </p>
                    )}
                    <Button
                        onClick={() => navigate("/")}
                        className="w-full rounded-full bg-aiblue hover:bg-blue-700 text-white font-semibold py-5"
                    >
                        Back to Home
                    </Button>
                </motion.div>
            </div>
        );
    }

    // ── Enrollment form ──────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 py-16 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Back link */}
                <Link
                    to="/cs-programme"
                    className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-8 transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to Programme
                </Link>

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                        <GraduationCap className="h-3.5 w-3.5" />
                        BTech Computer Science with Specialization in AI and ML
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl text-gray-900 leading-tight">
                        Reserve Your Seat
                    </h1>
                    <p className="mt-3 text-gray-500">
                        Fill in your details and pay ₹1 to reserve your seat in the programme. Our team will reach out with next steps.
                    </p>
                </motion.div>

                {/* Form card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden"
                >
                    {/* Student section */}
                    <div className="p-8 pb-6 border-b border-gray-100">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <GraduationCap className="h-4 w-4 text-blue-600" />
                            </div>
                            <h2 className="font-bold text-gray-900">Student Details</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <TextField
                                label="Full Name" name="full_name"
                                form={form} set={set} errors={errors}
                                placeholder="Arjun Sharma" required
                            />
                            <TextField
                                label="Email Address (optional)" name="email" type="email"
                                form={form} set={set} errors={errors}
                                placeholder="arjun@example.com"
                            />
                            <TextField
                                label="Mobile Number (optional)" name="mobile_number" type="tel"
                                form={form} set={set} errors={errors}
                                placeholder="9876543210"
                            />
                            <TextField
                                label="City" name="city_location"
                                form={form} set={set} errors={errors}
                                placeholder="Hyderabad" required
                            />
                        </div>
                    </div>

                    {/* Parent section */}
                    <div className="p-8 pb-6">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                                <Users className="h-4 w-4 text-orange-500" />
                            </div>
                            <h2 className="font-bold text-gray-900">Parent / Guardian Details</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <TextField
                                label="Parent / Guardian Name" name="parent_name"
                                form={form} set={set} errors={errors}
                                placeholder="Rajesh Sharma" required
                                colSpan
                            />
                            <TextField
                                label="Parent Mobile Number" name="parent_mobile" type="tel"
                                form={form} set={set} errors={errors}
                                placeholder="9876543200" required
                            />
                            <TextField
                                label="Parent Email" name="parent_email" type="email"
                                form={form} set={set} errors={errors}
                                placeholder="rajesh@example.com" required
                            />
                        </div>
                    </div>

                    {/* Payment section */}
                    <div className="px-8 pb-8">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-6 border border-blue-100">
                            <p className="text-sm font-semibold text-gray-700 mb-3">Reservation Fee (₹)</p>
                            <div className="flex items-center gap-3">
                                <span className="text-2xl font-black text-gray-500">₹</span>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="Enter amount"
                                    value={form.amount ?? ""}
                                    onChange={(e) => set("amount", e.target.value)}
                                    className="flex-1 h-12 rounded-xl border border-gray-200 px-4 text-xl font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-aiblue bg-white"
                                />
                            </div>
                            {errors.amount && <p className="mt-1 text-xs text-red-500 font-medium">{errors.amount}</p>}
                            <p className="text-xs text-gray-400 mt-2">One-time seat reservation fee</p>
                        </div>

                        <Button
                            onClick={handlePayment}
                            disabled={submitting}
                            className="w-full rounded-2xl bg-aiblue hover:bg-blue-700 text-white font-bold py-6 text-base shadow-lg shadow-blue-200 transition-all"
                        >
                            {submitting ? "Processing…" : "Pay ₹1 & Reserve Seat"}
                        </Button>

                        <p className="text-center text-xs text-gray-400 mt-4">
                            Secured by{" "}
                            <span className="font-semibold text-gray-500">Razorpay</span>.
                            Your payment information is encrypted and safe.
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
